import * as React from 'react';
import { type LucideProps } from 'lucide-react';
import * as LucideIcons from 'lucide-react';
import { useIconRegistry } from './context';
import { devWarn, IS_PROD } from '../kit/internal/devWarn';
import type { IconIntent } from './registry';

export interface JIconProps extends Omit<LucideProps, 'ref'> {
  /** Semantic icon intent from the registry. */
  intent: IconIntent;
  /**
   * When true, the icon is purely decorative — emits
   * `aria-hidden="true"` and `focusable="false"` together (the
   * canonical dual-attribute pattern that both removes the SVG
   * from the accessibility tree AND defeats the legacy IE/Edge
   * SVG-is-tabbable behavior).
   *
   * Default: false (preserves v4 behavior — no auto a11y emission).
   * Adopters should pass `decorative` for icons inside a labeled
   * control (IconButton, Button with icon-leading, etc.) and pass
   * `aria-label` for standalone informative icons.
   *
   * `decorative` and `aria-label` are mutually exclusive — passing
   * both is invalid ARIA (`aria-hidden` on a labeled element hides
   * the label too). Dev mode warns once per intent per session if
   * neither is set on a non-decorative icon.
   */
  decorative?: boolean;
}

/* Inline SVG fallback paths for the small set of intents that
   Jacquard primitives reference for visible icon-only controls.
   Codex iter-8 #high: in the UMD bundle, `lucide-react` is an
   external — when `window.lucide` is absent (the documented UMD
   install only pulls React + ReactDOM + kit.js), JIcon used to
   return `null` and icon-only buttons (Transfer's action column,
   the calendar nav arrows, etc.) rendered blank. The fallback set
   covers every intent used by an icon-only control inside the
   shipping kit so UMD consumers always get a visible glyph. */
const FALLBACK_PATHS: Partial<Record<IconIntent, string>> = {
  'chevron-left': 'M15 18l-6-6 6-6',
  'chevron-right': 'M9 18l6-6-6-6',
  'chevron-up': 'M6 15l6-6 6 6',
  'chevron-down': 'M6 9l6 6 6-6',
  'arrow-left': 'M19 12H5M12 19l-7-7 7-7',
  'arrow-right': 'M5 12h14M12 5l7 7-7 7',
  'arrows-right-left': 'M16 3l4 4-4 4M20 7H4M8 21l-4-4 4-4M4 17h16',
  close: 'M18 6L6 18M6 6l12 12',
  search: 'M21 21l-4.35-4.35M17 10a7 7 0 11-14 0 7 7 0 0114 0z',
  calendar: 'M3 4h18v18H3zM3 10h18M16 2v4M8 2v4',
  clock: 'M12 6v6l4 2M21 12a9 9 0 11-18 0 9 9 0 0118 0z',
  check: 'M5 13l4 4L19 7',
  plus: 'M12 5v14M5 12h14',
  info: 'M12 8v4M12 16h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z',
  menu: 'M3 6h18M3 12h18M3 18h18',
  star: 'M12 2l3.09 6.26 6.91 1-5 4.87 1.18 6.87L12 17.77l-6.18 3.23L7 14.13l-5-4.87 6.91-1z',
  pin: 'M12 17v5M9 10.76a2 2 0 01-1.11 1.79l-1.78.9A2 2 0 005 15.24V16a1 1 0 001 1h12a1 1 0 001-1v-.76a2 2 0 00-1.11-1.79l-1.78-.9A2 2 0 0115 10.76V7a1 1 0 011-1 2 2 0 000-4H8a2 2 0 000 4 1 1 0 011 1z',
  heart:
    'M20.84 4.61a5.5 5.5 0 00-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 00-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 000-7.78z',
  user: 'M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2M16 7a4 4 0 11-8 0 4 4 0 018 0z',
  edit: 'M11 4H4v16h16v-7M18.5 2.5a2.121 2.121 0 113 3L12 15l-4 1 1-4z',
  trash: 'M3 6h18M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2',
  share: 'M4 12v8a2 2 0 002 2h12a2 2 0 002-2v-8M16 6l-4-4-4 4M12 2v13',
  settings:
    'M12 15a3 3 0 100-6 3 3 0 000 6zM19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 01-2.83 2.83l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83-2.83l.06-.06a1.65 1.65 0 00.33-1.82 1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 012.83-2.83l.06.06a1.65 1.65 0 001.82.33H9a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 2.83l-.06.06a1.65 1.65 0 00-.33 1.82V9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z',
  warning:
    'M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0zM12 9v4M12 17h.01',
  success: 'M22 11.08V12a10 10 0 11-5.93-9.14M22 4L12 14.01l-3-3',
  error: 'M7.86 2h8.28L22 7.86v8.28L16.14 22H7.86L2 16.14V7.86L7.86 2zM15 9l-6 6M9 9l6 6',
};

/* Generic glyph for intents we don't have an inline fallback for. */
const FALLBACK_GENERIC = 'M12 12m-3 0a3 3 0 106 0 3 3 0 10-6 0';

/* FallbackSvg props mirror the SVG attributes that Lucide accepts so
   rest-prop spread is type-safe. Audit #29 (2026-05-16): replaces the
   prior `[k: string]: any` index signature; rest props now narrow
   correctly to SVGSVGElement attributes. */
interface FallbackSvgProps extends Omit<
  React.SVGAttributes<SVGSVGElement>,
  'width' | 'height' | 'fill' | 'strokeWidth'
> {
  intent: IconIntent;
  size?: number | string;
  strokeWidth?: number | string;
  fill?: string;
}

const FallbackSvg = React.forwardRef<SVGSVGElement, FallbackSvgProps>(function FallbackSvg(
  { intent, size = 24, strokeWidth = 2, fill = 'none', ...rest },
  ref,
) {
  const d = FALLBACK_PATHS[intent] ?? FALLBACK_GENERIC;
  return (
    <svg
      ref={ref}
      xmlns="http://www.w3.org/2000/svg"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill={fill}
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      {...rest}
    >
      <path d={d} />
    </svg>
  );
});

/**
 * Internal icon component for Jacquard kit primitives.
 * Resolves intent names to Lucide components via the registry.
 *
 * When wrapped in a <JacquardIconProvider>, overridden intents
 * are picked up automatically via context.
 *
 * For adopter code, prefer direct per-icon Lucide imports for
 * tree-shaking (e.g., `import { X } from 'lucide-react'`).
 *
 * UMD safety: if `lucide-react` resolves to an undefined module
 * (the documented UMD install path doesn't bundle lucide), JIcon
 * falls back to a small set of inline SVG paths covering every
 * intent referenced by an icon-only control inside the kit.
 * Icon-only buttons therefore always render a visible glyph.
 */
/* Audit #29 (2026-05-16): tightened `any`-typed Lucide lookup to a
   `LucideProps`-shaped component type. The lookup is dynamic (intent
   resolves to a Lucide named export at runtime), so a generic
   ComponentType is the narrowest type without enumerating every
   Lucide icon. The cast at the lookup site replaces the prior
   `any` cast plus eslint-disable. */
type LucideIconComponent = React.ComponentType<LucideProps & React.RefAttributes<SVGSVGElement>>;
const LUCIDE_NAMESPACE = LucideIcons as unknown as Record<string, LucideIconComponent | undefined>;

/* Module-level dedupe for the "missing a11y" dev warning so apps
   that render the same intent in many places get one warning per
   intent per session, not one per render. */
const _warnedMissingA11y = new Set<string>();

export const JIcon = React.forwardRef<SVGSVGElement, JIconProps>(function JIcon(
  { intent, decorative, ...props },
  ref,
) {
  const registry = useIconRegistry();
  const lucideName = registry[intent];
  let Icon: LucideIconComponent | undefined;
  try {
    /* Two failure modes covered by the try/catch:
       1. UMD: `lucide-react` is an external; `LucideIcons` may be
          undefined or empty. Optional chain handles undefined; the
          catch is just belt-and-suspenders.
       2. Strict-ESM-mock test environments: vitest's auto-spy can
          throw when accessing an unmocked named export. The catch
          drops through to the inline fallback. */
    Icon = LUCIDE_NAMESPACE?.[lucideName];
  } catch {
    Icon = undefined;
  }

  /* Decorative SVG: aria-hidden=true AND focusable=false (the dual-
     attribute pattern per MDN SVG accessibility). Adopter-supplied
     `aria-*` props in `...props` would precede these, but the
     spread order below puts the decorative pair AFTER, so we don't
     silently let an aria-label sneak past while the icon is hidden. */
  const a11yProps = decorative ? ({ 'aria-hidden': true, focusable: 'false' } as const) : undefined;

  /* Dev-time guard: an informative icon (non-decorative) without a
     label is an a11y violation in waiting. Checks aria-label /
     aria-labelledby / aria-hidden (the last for back-compat with
     v4 call sites that pre-dated `decorative`). One warn per
     intent per session. */
  if (!IS_PROD && !decorative) {
    const p = props as Record<string, unknown>;
    if (
      p['aria-label'] === undefined &&
      p['aria-labelledby'] === undefined &&
      p['aria-hidden'] === undefined &&
      !_warnedMissingA11y.has(intent)
    ) {
      _warnedMissingA11y.add(intent);
      devWarn(`[JKit.JIcon] intent="${intent}" needs \`decorative\` or \`aria-label\`.`);
    }
  }

  if (Icon) {
    return <Icon ref={ref} {...props} {...a11yProps} />;
  }
  return <FallbackSvg ref={ref} intent={intent} {...props} {...a11yProps} />;
});
